import { useState } from 'react';
import { useRouter } from 'next/router';

import { Box, Form, Toast, useToast } from '@zillow/constellation-10';

import { ActionMetadataEntryPoint } from '@zg-rentals/ts-z-calendar-client';

import { useTourSchedulingContext } from '../../../../contexts/TourSchedulingContext';
import { useGetListingDetails } from '../../../../hooks/queries/properties';
import accessUtils from '../../../../utils/accessUtils';

import TourSchedulingBookingMethodBody from '../../../../../srcLegacy/shared/components/WizardModularListing/TourSchedulingSetup/components/TourSchedulingBookingMethodBody';

import { wizardFlexContainerStyles } from '../../../../styles/wizard';

import SpinnerContainer from '../../../SpinnerContainer';
import PageWizard from '../../../Wizard/PageWizard';

import type { TimeZoneIds } from '@zg-rentals/touring';
import type { WizardBodyTemplateProps } from '../../../WizardStepController';

const TourSchedulingBookingMethod = ({
  onGoToNextStep,
  onGoToPrevStep,
  onExitWizard,
  wizardTitle,
  progressStepper,
  totalSteps,
  currentStepIndex,
  showDesktopViewBreakpoint,
  ...rest
}: WizardBodyTemplateProps) => {
  const router = useRouter();
  const { enqueueToast } = useToast();
  const { selectedBookingMethod, setSelectedBookingMethod, selectedTourDuration, tourCalendarApiHook } =
    useTourSchedulingContext();
  const listingAlias = router.query.listingAlias as string;

  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const canAccessRelistingFlow = accessUtils.canAccessRelistingFlow({
    canBeRelisted: listingDetails?.canBeRelisted ?? false,
  });

  const [isNavigating, setIsNavigating] = useState(false);

  const onNextClick = async () => {
    if (isNavigating) return;
    setIsNavigating(true);

    try {
      if (!selectedBookingMethod && tourCalendarApiHook.isTouringSchedulingEnabled) {
        await tourCalendarApiHook.disableTouringForListing();
      }

      // When zoneIdWithoutTourConfig exists, the listing still has its original timezone stored
      // (touring was auto-disabled after 14+ days inactive), so skip the timezone step
      if (
        canAccessRelistingFlow &&
        selectedBookingMethod &&
        tourCalendarApiHook.initialZoneIdWithoutTourConfig &&
        selectedBookingMethod !== tourCalendarApiHook.tourConfirmationType
      ) {
        await tourCalendarApiHook.enableTouringForListing(
          selectedBookingMethod,
          tourCalendarApiHook.initialZoneIdWithoutTourConfig as TimeZoneIds,
          selectedTourDuration,
          ActionMetadataEntryPoint.REACTIVATION_LISTING,
        );
      }

      onGoToNextStep?.();
    } catch {
      enqueueToast(
        <Toast tone="critical">Your tour setting changes couldn’t be saved. Try again in a few moments.</Toast>,
      );
    } finally {
      setIsNavigating(false);
    }
  };

  return (
    <PageWizard
      {...rest}
      title=""
      wizardTitle={wizardTitle}
      progressStepper={progressStepper}
      totalSteps={totalSteps}
      currentStepIndex={currentStepIndex}
      onNextClick={onNextClick}
      onBackClick={onGoToPrevStep || undefined}
      onExitTriggered={onExitWizard}
      showDesktopViewBreakpoint={showDesktopViewBreakpoint}
      isNextDisabled={tourCalendarApiHook.isDisableTouringForListingLoading || isNavigating}
    >
      <div data-testid="TourSchedulingBookingMethod">
        {tourCalendarApiHook.isGetTourConfigAndAvailabilityLoading ? (
          <SpinnerContainer />
        ) : (
          <Box css={wizardFlexContainerStyles}>
            <Form asChild onSubmit={(e) => e.preventDefault()}>
              <TourSchedulingBookingMethodBody
                selectedBookingMethod={selectedBookingMethod}
                onSelectBookingMethod={setSelectedBookingMethod}
              />
            </Form>
          </Box>
        )}
      </div>
    </PageWizard>
  );
};

export default TourSchedulingBookingMethod;
